import Link from "next/link";
import { cn } from "@/lib/utils";
import {
  formatTalkListDate,
  isHotTalkPost,
  talkPostHref,
  type TalkPost,
} from "@/lib/community-talk";

interface TalkTableProps {
  posts: TalkPost[];
  /** 표 맨 위에 고정하는 관리자 공지(페이지·탭과 무관). 목록 페이지만 넘긴다. */
  notices?: TalkPost[];
  /** 상세 페이지 하단 목록에서 현재 글을 강조한다. */
  currentId?: number | null;
  /** 상세 하단용 축약 표(헤더 생략, 여백 축소). */
  compact?: boolean;
  /** 날짜 표기 기준 시각(서버 렌더 시 한 번 잡아 행마다 동일 기준을 쓴다). */
  now: Date;
  emptyText?: string;
}

const th = "px-2 py-2 text-xs font-semibold text-muted-foreground whitespace-nowrap";
const td = "px-2 py-2 align-middle";

/** 열 폭·가시성은 `th`와 `td`가 반드시 같아야 해서(아래 주석) 열마다 한 곳에서 정의한다. */
const colAuthor = "hidden w-20 sm:table-cell";
const colDate = "w-14 sm:w-16";
const colViews = "hidden w-12 sm:table-cell";
const colRecommend = "w-12";

/**
 * 디시인사이드식 표 목록: 제목 [댓글] · 글쓴이 · 날짜 · 조회 · 추천. 추천글은 제목 앞 배지.
 * 관리자 공지(`notices`)는 "공지" 배지 + 연한 배경으로 맨 위에 고정한다.
 * (번호 열은 3컬럼 셸의 좁은 본문에서 제목 폭을 잡아먹어 뺐다.)
 * 모바일(<640px)에서는 글쓴이·조회 열을 숨기고 제목 아래에 글쓴이·조회를 겹쳐 보여준다.
 *
 * 열 너비는 `<colgroup>`이 아니라 셀 자체에 준다(JCG-3618). WebKit(iOS Safari)은 `<col>`의
 * `display: none`을 무시해서, 숨긴 열의 `<col>`이 남은 채 보이는 셀이 앞 열부터 순서대로
 * 배정된다. 그러면 모바일에서 제목·날짜·추천이 1~3번 열 폭을 쓰고 4~5번 열이 빈 채로 남아
 * 표 오른쪽이 비어 보인다. 셀에 폭을 주면 열 개수가 렌더된 셀 수와 항상 같아 엔진과 무관하게
 * 남은 폭이 제목 열로 간다. (`table-fixed`는 첫 행의 셀 폭을 쓰므로 `compact`로 thead가 없는
 * 경우를 위해 `th`뿐 아니라 `td`에도 같은 폭을 준다. 둘이 어긋나면 모드에 따라 폭이 갈리므로
 * 위의 `col*` 상수를 양쪽에서 함께 쓴다.)
 *
 * 같은 이유로 빈 목록 안내는 `colSpan`을 쓴 행이 아니라 표 바깥 문단으로 그린다(JCG-3618).
 * 표의 열 개수는 가장 많은 열을 가진 행이 정하므로, 모바일에서 3열짜리 행만 있는 표에
 * `colSpan={5}` 행을 하나 두면 4~5번 열이 생겨 나머지 행이 표 폭을 다 못 채운다.
 */
export default function TalkTable({
  posts,
  notices = [],
  currentId = null,
  compact = false,
  now,
  emptyText,
}: TalkTableProps) {
  const renderRow = (post: TalkPost, notice: boolean) => {
    const hot = !notice && isHotTalkPost(post);
    const isCurrent = currentId === post.id;
    return (
      <tr
        key={`${notice ? "notice" : "post"}-${post.id}`}
        className={cn(
          "transition-colors hover:bg-amber-50/60 dark:hover:bg-amber-950/20",
          notice && "bg-amber-50/40 dark:bg-amber-950/10",
          isCurrent && "bg-amber-50 dark:bg-amber-950/30"
        )}
      >
        <td className={cn(td, "min-w-0")}>
          <Link
            href={talkPostHref(post.id)}
            prefetch={false}
            className={cn(
              "flex items-center gap-1.5 text-[15px] leading-snug text-[#333] hover:underline dark:text-neutral-100",
              (isCurrent || notice) && "font-semibold"
            )}
          >
            {notice && (
              <span className="shrink-0 rounded bg-amber-500 px-1 text-[10px] font-semibold text-white dark:bg-amber-400 dark:text-amber-950">
                공지
              </span>
            )}
            {hot && (
              <span className="shrink-0 rounded bg-amber-100 px-1 text-[10px] font-semibold text-amber-800 dark:bg-amber-900 dark:text-amber-200">
                추천글
              </span>
            )}
            <span className="truncate">{post.title}</span>
            {(post.commentCount ?? 0) > 0 && (
              <span className="shrink-0 text-xs font-semibold text-amber-700 dark:text-amber-300">
                [{post.commentCount}]
              </span>
            )}
            {post.imageUrls.length > 0 && (
              <span className="shrink-0 text-xs text-muted-foreground" aria-label="이미지 첨부">🖼</span>
            )}
          </Link>
          <p className="mt-0.5 truncate text-xs text-muted-foreground sm:hidden">
            {post.author}
            <span aria-hidden> · </span>조회 {post.viewCount}
          </p>
        </td>
        <td className={cn(td, colAuthor, "truncate text-center text-xs")}>
          <span className="truncate">{post.author}</span>
        </td>
        <td className={cn(td, colDate, "text-center font-mono text-xs text-muted-foreground")}>
          <time dateTime={post.createdAt}>{formatTalkListDate(post.createdAt, now)}</time>
        </td>
        <td className={cn(td, colViews, "text-center font-mono text-xs text-muted-foreground")}>
          {post.viewCount}
        </td>
        <td className={cn(td, colRecommend, "text-center font-mono text-xs", hot ? "font-semibold text-amber-700 dark:text-amber-300" : "text-muted-foreground")}>
          {post.recommendCount}
        </td>
      </tr>
    );
  };

  return (
    <div className="overflow-x-auto rounded-md border border-gray-200 bg-white dark:border-neutral-700 dark:bg-neutral-900/40">
      <table className="w-full table-fixed border-collapse text-sm">
        {!compact && (
          <thead className="border-b border-gray-200 bg-gray-50 dark:border-neutral-700 dark:bg-neutral-800/60">
            <tr>
              <th scope="col" className={cn(th, "text-left")}>제목</th>
              <th scope="col" className={cn(th, colAuthor, "text-center")}>글쓴이</th>
              <th scope="col" className={cn(th, colDate, "text-center")}>날짜</th>
              <th scope="col" className={cn(th, colViews, "text-center")}>조회</th>
              <th scope="col" className={cn(th, colRecommend, "text-center")}>추천</th>
            </tr>
          </thead>
        )}
        <tbody className="divide-y divide-gray-100 dark:divide-neutral-800">
          {notices.map((post) => renderRow(post, true))}
          {posts.map((post) => renderRow(post, false))}
        </tbody>
      </table>
      {posts.length === 0 && (
        <p
          className={cn(
            "px-4 py-12 text-center text-sm text-[#706E6E] dark:text-neutral-400",
            notices.length > 0 && "border-t border-gray-100 dark:border-neutral-800"
          )}
        >
          {emptyText ?? "아직 글이 없어요. 첫 글을 남겨보세요."}
        </p>
      )}
    </div>
  );
}
